<template>
    <div class="table-wrapper">
        <table class="table table-filter table--striped table--filterable" v-if="tableData && tableData.length > 0">
            <thead>
                <tr>
                    <th v-for="(column, index) in columns" :key="index" class="table-filter__trigger" :width="column.width">
                        <div v-if="column.available === undefined || checkIfAvailable(column.available.type)">
                            {{ $t(column.name) }}
                        </div>

                        <div
                            v-if="column.sortable"
                            class="table-filter__sorting"
                            @click="sortClickHandler(column.field)"
                            :class="{
                                asc: activeSorting[column.field] === 'asc',
                                desc: activeSorting[column.field] === 'desc',
                            }"
                        >
                            <i class="icon icon--common-caret"></i>
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(item, index) in filteredData" :key="index">
                    <td v-for="(column, cIndex) in columns" :key="cIndex" :class="{ centered: column.type === 'alarm-message' }">
                        <router-link
                            v-if="column.type === 'link'"
                            :to="{
                                name: column.linkTo.name,
                                params: {
                                    [column.linkTo.linkParam]: item[column.linkTo.linkParam],
                                },
                            }"
                        >
                            {{ item[column.field] }}
                        </router-link>
                        <div class="table__input-holder" v-else-if="column.type === 'select'">
                            <select
                                :disabled="column.disabled && checkIfDisabled(column.disabled.ability) && column.disabled.fieldValue === item[column.field]"
                                class="form__field"
                                v-model="item[column.field]"
                                @change="
                                    $emit(column.inputEmitEvent, {
                                        id: item.id,
                                        value: item[column.field],
                                        field: column.field,
                                    })
                                "
                            >
                                <option v-for="(option, index) in column.options" :key="index" :label="$t(option.label)" :value="option.value" :disabled="checkIfDisabled(option.disabled)">
                                    {{ $t(option.label) }}
                                </option>
                            </select>
                        </div>
                        <span v-else-if="column.type === 'remove'">
                            <a v-if="checkIfAvailable(column.available.type, item[column.available.field])" class="button button--attention" @click="removeItem(item.id)">
                                <i class="icon icon--2x icon--action-decline"></i>
                            </a>
                        </span>
                        <span v-else-if="column.type === 'alarm-message'">
                            <alarm-message :alarm="item[column.field]" :tooltip="true" />
                        </span>
                        <span v-else-if="column.type === 'labels'">
                            <SystemLabels :system="item" />
                        </span>
                        <span v-else-if="column.type === 'yesno'">
                            <span v-if="item[column.field]">
                                {{ $t('common.yes') }}
                            </span>
                            <span v-else>
                                {{ $t('common.no') }}
                            </span>
                        </span>
                        <span v-else>
                            <span v-if="column.fields">
                                <span v-for="(f, index) in column.fields" :key="f">
                                    {{ f === 'country' ? (item[f] ? item[f].toUpperCase() : '') : item[f] }}{{ index < column.fields.length - 1 && item[f] && f !== 'postcode' ? ', ' : '' }}
                                </span>
                            </span>
                            <span v-else :class="{ uppercase: column.field === 'country' }">
                                {{ item[column.field] }}
                            </span>
                        </span>
                    </td>
                </tr>
            </tbody>

            <TablePagination
                v-if="paginationData"
                :paginationData="paginationData"
                :visibleAmount="searchedTableData ? searchedTableData.length : tableData.length"
                @goToPrevPage="prevPageHandler"
                @goToNextPage="nextPageHandler"
            />
        </table>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import AlarmMessage from '@/components/AlarmMessage';
import TablePagination from '@/components/TablePagination';
import SystemLabels from '@/components/SystemLabels';

export default {
    components: {
        AlarmMessage,
        TablePagination,
        SystemLabels,
    },
    props: {
        columns: {
            type: [Boolean, Array],
            default: false,
        },
        tableData: {
            type: [Boolean, Array],
            default: false,
        },
        searchedTableData: {
            type: [Boolean, Array],
            default: false,
        },
        paginationData: {
            type: [Boolean, Object],
            default: false,
        },
        activeSorting: {
            type: [Boolean, String],
            default: false,
        },
    },
    computed: {
        ...mapState(['activeOrganization']),
        ...mapGetters(['isParentOrganization']),
        filteredData() {
            if (!this.tableData) return [];

            let filtered = this.searchedTableData ? [...this.searchedTableData] : [...this.tableData];
            return filtered;
        },
        activePage() {
            return this.paginationData ? this.paginationData.current_page : 0;
        },
    },
    methods: {
        prevPageHandler() {
            this.$emit('goToPage', this.activePage - 1);
        },
        nextPageHandler() {
            this.$emit('goToPage', this.activePage + 1);
        },
        sortClickHandler(value) {
            this.$emit('sortBy', value);
        },
        removeItem(id) {
            this.$emit('removeItem', id);
        },
        checkIfDisabled(type) {
            if (type === 'isNotOrganizationOwner' && this.activeOrganization) {
                const canEdit = this.$can('edit', 'Organization owner');
                const isParentOrganization = this.isParentOrganization(this.activeOrganization.breadcrumbs);
                return !(canEdit || isParentOrganization);
            }
            return false;
        },
        checkIfAvailable(type, fieldValue) {
            if (type === 'isOwnerOrHigher' && this.activeOrganization) {
                const isOwner = fieldValue === 'owner';
                if (isOwner) {
                    const canEdit = this.$can('edit', 'Organization owner');
                    const isParentOrganization = this.isParentOrganization(this.activeOrganization.breadcrumbs);
                    return canEdit || isParentOrganization;
                }
            } else if (type === 'canDeleteSubOrg') {
                return this.$can('remove', 'Suborganization');
            }
            return true;
        },
    },
};
</script>
